import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';

import { ArrowRight, ShieldCheck } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { useI18n } from '@/i18n/I18nContext';
const wordmarkFull = '/assets/wordmark-sabaody-island-full.webp';
const wordmarkMd = '/assets/wordmark-sabaody-island-md.webp';

/* Below-the-fold sections — lazy-loaded to reduce initial JS */
const BubbleNav = React.lazy(() => import('@/components/BubbleNav'));
const HomeReviewsSection = React.lazy(() => import('@/components/HomeReviewsSection'));
const CampaignBanner = React.lazy(() => import('@/components/CampaignBanner'));

/** Placeholder that reserves vertical space while lazy chunks load */
const SectionSkeleton: React.FC<{ minH: string }> = ({ minH }) => (
  <div style={{ minHeight: minH }} aria-hidden="true" />
);

const Index: React.FC = () => {
  const { t, localePath } = useI18n();


  return (
    <main>
      <SEOHead
        title={t('seo.home_title')}
        description={t('seo.home_desc')}
        canonicalPath="/"
      />

      <Suspense fallback={<div style={{ minHeight: 88 }} aria-hidden="true" />}>
        <CampaignBanner />
      </Suspense>


      {/* HERO */}
      <section
        className="relative overflow-hidden flex items-center justify-center min-h-[520px] md:min-h-[640px] bg-cover bg-no-repeat bg-[center_top] md:bg-center"
        style={{
          backgroundImage: "url('/assets/hero-sabaody-bubbles.webp')",
          maxHeight: '820px',
        }}
      >
        {/* Bottom fade that blends into the page background */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, hsl(195 30% 97% / 0.4) 70%, hsl(195 30% 97%) 100%)' }} />

        <div className="relative w-full max-w-[900px] mx-auto px-4 py-10 md:py-16 flex flex-col items-center justify-center text-center">
            <div
              className="flex flex-col items-center animate-[heroFadeIn_0.6s_ease-out_both]"
            >
             <img
                src={wordmarkMd}
                srcSet={`${wordmarkMd} 440w, ${wordmarkFull} 840w`}
                sizes="(max-width: 768px) 220px, (max-width: 1024px) 340px, 420px"
                alt="Sabaody Island — Boutique européenne de cartes One Piece TCG"
                className="w-[220px] md:w-[340px] lg:w-[420px] h-auto drop-shadow-lg"
                width={420}
                height={280}
                {...({ fetchpriority: 'high' } as any)}
                loading="eager"
                decoding="sync"
             />
             <h1
               className="mt-4 text-xl md:text-2xl font-heading font-bold tracking-tight"
               style={{ color: 'hsl(195 60% 28%)', textShadow: '0 1px 3px rgba(255,255,255,0.5)' }}
             >
               {t('hero.subtitle')}
             </h1>
            <div className="mt-6 md:mt-7">
              <Link to={localePath('/shop')} className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-2xl gradient-ocean text-primary-foreground font-heading font-bold text-lg shadow-elevated hover:opacity-90 transition-opacity">
                {t('hero.cta')} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-sm font-medium text-primary">
              <ShieldCheck className="w-4 h-4" />
              <span>{t('sealed.chip')}</span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2.5">
              {[
                { icon: '🛡️', key: 'trust.verified' },
                { icon: '🔄', key: 'trust.refund' },
                { icon: '🚚', key: 'trust.shipping' },
              ].map((b, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium select-none cursor-default"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    color: 'hsl(210 50% 25%)',
                  }}
                >
                  <span aria-hidden="true">{b.icon}</span> {t(b.key)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Smooth transition spacer */}
      <div className="py-8 md:py-12" />

      {/* Below-the-fold: lazy-loaded with space reservation */}
      <Suspense fallback={<SectionSkeleton minH="400px" />}>
        <BubbleNav />
      </Suspense>
      <Suspense fallback={<SectionSkeleton minH="520px" />}>
        <HomeReviewsSection />
      </Suspense>
    </main>
  );
};

export default Index;
